import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";

import { Cargando, EstadoVacio } from "../components/Estados";
import {
  asignarAuxiliar,
  obtenerAuxiliares,
  retirarAuxiliar,
} from "../services/bibliotecaApi";
import { momento } from "../model/presentacion";

/**
 * Por que no se puede elegir a esa persona para esa accion, o "" si se puede.
 * Es la misma regla que valida el backend: el horario.
 */
const impedimento = (candidato, accion) => {
  const opcion = candidato.acciones.find(({ codigo }) => codigo === accion);
  if (opcion && !opcion.en_horario) return "fuera de su horario";
  return "";
};

/**
 * Auxiliares de una solicitud, como en AGIL: quien entrega y quien retira
 * los libros, o quien capacita en ARIA. Solo se cambian mientras la
 * solicitud esta solicitada o revisada; despues queda de consulta.
 *
 * Cada cambio responde el estado completo, asi que la lista siempre es la que
 * dice el backend.
 */
const PanelAuxiliares = ({ solicitud, onCambio, onMensaje }) => {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState("");
  const [accion, setAccion] = useState("");
  const [persona, setPersona] = useState("");
  const [retirando, setRetirando] = useState(null);
  const [nota, setNota] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vigente = true;
    obtenerAuxiliares(solicitud.id)
      .then((respuesta) => vigente && setDatos(respuesta))
      .catch((error) => vigente && setFallo(error.message));
    return () => {
      vigente = false;
    };
    // Se consulta una vez al abrir; despues se actualiza con cada respuesta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud.id]);

  // Con una sola accion posible (ARIA: capacitar) queda elegida.
  useEffect(() => {
    if (datos?.acciones.length === 1) setAccion(datos.acciones[0].codigo);
  }, [datos]);

  const aplicar = async (peticion) => {
    setEnviando(true);
    const { ok, titulo, datos: nuevos } = await peticion;
    setEnviando(false);
    if (!ok) {
      onMensaje(titulo, "info");
      return false;
    }
    setDatos(nuevos);
    onCambio();
    return true;
  };

  const asignar = async () => {
    if (await aplicar(asignarAuxiliar(solicitud.id, persona, accion))) {
      onMensaje("Auxiliar asignado.", "success");
      setPersona("");
    }
  };

  const retirar = async () => {
    if (await aplicar(retirarAuxiliar(solicitud.id, retirando.id, nota.trim()))) {
      onMensaje("Auxiliar retirado.", "success");
      setRetirando(null);
      setNota("");
    }
  };

  if (fallo) return <EstadoVacio mensaje={fallo} />;
  if (!datos) return <Cargando />;

  const { maneja_auxiliares: maneja, acciones, asignados, retirados, candidatos } = datos;
  const elegido = candidatos.find(({ id }) => id === persona);

  return (
    <div>
      <Typography variant="subtitle2">Asignados</Typography>
      {asignados.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Aún no hay auxiliares asignados.
        </Typography>
      ) : (
        <List dense disablePadding>
          {asignados.map((asignado) => (
            <ListItem key={asignado.id} disableGutters>
              <ListItemText
                primary={asignado.auxiliar.nombre}
                secondary={`${asignado.accion_nombre} · desde ${momento(asignado.fecha_registro)}`}
              />
              {maneja && (
                <ListItemSecondaryAction>
                  <Tooltip title="Retirar">
                    <IconButton edge="end" size="small" onClick={() => setRetirando(asignado)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {retirando && (
        <Grid container spacing={1} alignItems="flex-end" style={{ marginTop: 8 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label={`Nota para retirar a ${retirando.auxiliar.nombre} (opcional)`}
              value={nota}
              onChange={({ target }) => setNota(target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button onClick={() => setRetirando(null)}>No</Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button color="secondary" disabled={enviando} onClick={retirar}>
              Retirar
            </Button>
          </Grid>
        </Grid>
      )}

      {maneja ? (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 24 }}>
            Asignar
          </Typography>
          {candidatos.length === 0 ? (
            <Alert severity="info">
              Nadie de biblioteca gestiona este tipo de solicitud. Asigna el tipo
              en Administrar → ¡ Controlar Permisos !.
            </Alert>
          ) : (
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Acción"
                  value={accion}
                  onChange={({ target }) => {
                    setAccion(target.value);
                    setPersona("");
                  }}
                >
                  {acciones.map(({ codigo, nombre }) => (
                    <MenuItem key={codigo} value={codigo}>
                      {nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Auxiliar"
                  value={persona}
                  disabled={!accion}
                  onChange={({ target }) => setPersona(target.value)}
                >
                  {candidatos.map((candidato) => {
                    const motivo = accion ? impedimento(candidato, accion) : "";
                    return (
                      <MenuItem key={candidato.id} value={candidato.id} disabled={Boolean(motivo)}>
                        {candidato.nombre}
                        {motivo && ` (${motivo})`}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  color="primary"
                  disabled={!elegido || !accion || enviando}
                  onClick={asignar}
                >
                  Asignar
                </Button>
              </Grid>
            </Grid>
          )}
        </>
      ) : (
        <Alert severity="info" style={{ marginTop: 16 }}>
          Los auxiliares solo se cambian mientras la solicitud está solicitada o
          revisada, y con permiso sobre ese estado.
        </Alert>
      )}

      {retirados.length > 0 && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 24 }}>
            Retirados
          </Typography>
          <List dense disablePadding>
            {retirados.map((retirado) => (
              <ListItem key={retirado.id} disableGutters>
                <ListItemText
                  primary={`${retirado.auxiliar.nombre} · ${retirado.accion_nombre}`}
                  secondary={[
                    momento(retirado.fecha_elimina),
                    retirado.retirado_por && `por ${retirado.retirado_por}`,
                    retirado.nota_retiro,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );
};

PanelAuxiliares.propTypes = {
  solicitud: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  /** Se llama tras cada cambio, para refrescar el detalle de fondo. */
  onCambio: PropTypes.func.isRequired,
  onMensaje: PropTypes.func.isRequired,
};

export default PanelAuxiliares;
